import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'sonner';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import type { PinItemType } from '~/modules/moodboards/type';
import { useAuth } from '@modules/root/context';
import { isUserLoggedIn, useUser } from '@modules/user';
import { decodeHTMLString } from '~/modules/seo/util/decode-markdown';
import type { FollowSelectorItem } from '~/modules/moodboards/types';
import { resolveBoardUrl } from '../board-url';
import { ACTION_ADD_ITEMS_TO_MOOD_BOARD } from '~/routes/actions.add-items-to-mood-board';
import { assignDocumentLocation } from '~/modules/error/util/document-navigation';
import { REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE } from '~/routes/actions.remove-items-from-mood-board-by-page';
import {
    addPinMembership,
    addSavedPinKeys,
    cloneMembershipCache,
    lastUsedBoardIdAtom,
    makePinKey,
    pinMembershipCacheAtom,
    removePinMembership,
    removeSavedPinKeys,
    resolveTrackedPinId,
    savedPinKeysAtom,
} from '~/modules/moodboards/state/design-board-save-state';

interface ItemToFollow {
    id: number;
    libraryItemId?: number;
}

/**
 * Custom hook to handle following/unfollowing items on mood boards.
 *
 * @returns An object containing board save/remove handlers and isSavedToBoard.
 */
export const useHandleFollow = () => {
    const savedPinKeys = useAtomValue(savedPinKeysAtom);
    const pinMembershipCache = useAtomValue(pinMembershipCacheAtom);
    const setSavedPinKeys = useSetAtom(savedPinKeysAtom);
    const setPinMembershipCache = useSetAtom(pinMembershipCacheAtom);
    const setLastUsedBoardId = useSetAtom(lastUsedBoardIdAtom);
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { showAuthModal } = useAuth();

    /**
     * Checks if an item is already saved to a mood board.
     *
     * @param itemId - The ID of the item to check. For PhotoPins, this is the LibraryImageID.
     * @param itemType - The type of the item to check.
     * @returns True if the item is saved, false otherwise.
     */
    const isInDesignBoard = useCallback(
        (itemId: number, itemType?: PinItemType) => {
            return savedPinKeys.has(makePinKey(itemId, itemType));
        },
        [savedPinKeys],
    );

    /**
     * Helper function to show success toast after saving to a board.
     * The action is the Magic Island's arrow-in-a-white-circle, passed as a
     * custom element so sonner's default action-button styling stays off it.
     */
    const showSaveSuccessToast = useCallback(
        (selectedBoard: FollowSelectorItem) => {
            const boardTitle = decodeHTMLString(selectedBoard.title);
            const targetUrl = resolveBoardUrl(selectedBoard) ?? '';

            // Use window.location for navigation because Remix's navigate()
            // can be swallowed by a concurrent fetcher revalidation triggered
            // by the save action that fired just before this toast appeared.
            const handleNavigate = () => {
                toast.dismiss();
                if (targetUrl) {
                    if (isLoggedIn) {
                        assignDocumentLocation(targetUrl);
                    } else {
                        showAuthModal({
                            authSource: 'designBoard',
                            redirect: targetUrl,
                        });
                    }
                } else if (!isLoggedIn) {
                    showAuthModal({
                        authSource: 'designBoard',
                        redirect: '/app/boards',
                    });
                }
            };

            // The arrow leads somewhere for a resolvable board URL, or into
            // the auth modal for logged-out users; otherwise omit it.
            const showViewAction = Boolean(targetUrl) || !isLoggedIn;

            toast.success(`Saved to ${boardTitle}`, {
                action: showViewAction ? (
                    <button
                        type="button"
                        aria-label="View board"
                        onClick={handleNavigate}
                        className="group/view flex size-13.5 shrink-0 items-center justify-center rounded-full bg-white"
                    >
                        <ArrowRightIcon className="size-4.5 transition-transform duration-200 ease-out group-hover/view:translate-x-0.5" />
                    </button>
                ) : undefined,
            });
        },
        [isLoggedIn, showAuthModal],
    );

    /**
     * Handles saving items to a specific board.
     * Used by TileFollowButtonWithPopover when a board is selected.
     *
     * @param selectedBoard - The board to save to.
     * @param items - The item(s) to save.
     * @param itemType - The type of items being saved.
     * @param options - `showSuccessToast: false` when the caller reports the
     * outcome itself (e.g. the save gate's success step). Failures always toast.
     * @returns `true` if save was successful.
     */
    const handleSaveToBoard = useCallback(
        async (
            selectedBoard: FollowSelectorItem,
            items: ItemToFollow | ItemToFollow[],
            itemType: PinItemType,
            options?: { showSuccessToast?: boolean },
        ): Promise<boolean> => {
            const itemsToFollow = (
                Array.isArray(items) ? items : [items]
            ).filter((item) => item.id !== 0);

            if (!itemsToFollow.length) {
                return false;
            }

            let previousSavedPinKeys = new Set(savedPinKeys);
            let previousMembershipCache =
                cloneMembershipCache(pinMembershipCache);

            // Add items to the selected board
            try {
                const itemIds = itemsToFollow.map(({ id }) => id);
                const pinKeys = itemsToFollow.map(({ id, libraryItemId }) =>
                    makePinKey(
                        resolveTrackedPinId(id, libraryItemId, itemType),
                        itemType,
                    ),
                );
                previousSavedPinKeys = new Set(savedPinKeys);
                previousMembershipCache =
                    cloneMembershipCache(pinMembershipCache);

                // Optimistic updates for instant UI feedback.
                setSavedPinKeys((prev: Set<string>) =>
                    addSavedPinKeys(prev, pinKeys),
                );
                setPinMembershipCache((prev) =>
                    addPinMembership(prev, pinKeys, selectedBoard.id),
                );
                setLastUsedBoardId(selectedBoard.id);

                const formData = new FormData();
                formData.append('boardId', String(selectedBoard.id));
                formData.append('itemsType', itemType);
                itemIds.forEach((itemId) => {
                    formData.append('itemIds[]', String(itemId));
                });

                const response = await fetch(ACTION_ADD_ITEMS_TO_MOOD_BOARD, {
                    method: 'post',
                    credentials: 'same-origin',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error('Save to mood board failed');
                }

                if (options?.showSuccessToast !== false) {
                    showSaveSuccessToast(selectedBoard);
                }

                return true;
            } catch (error) {
                console.error('Error adding items to board:', error);
                // Roll back optimistic updates when the request fails.
                setSavedPinKeys(previousSavedPinKeys);
                setPinMembershipCache(previousMembershipCache);
                toast.error('Failed to save to board');
                return false;
            }
        },
        [
            savedPinKeys,
            pinMembershipCache,
            showSaveSuccessToast,
            setSavedPinKeys,
            setPinMembershipCache,
            setLastUsedBoardId,
        ],
    );

    /**
     * Helper function to show success toast after removing from a board.
     */
    const showRemoveSuccessToast = useCallback(
        (selectedBoard: FollowSelectorItem) => {
            const boardTitle = decodeHTMLString(selectedBoard.title);
            toast.success(`Removed from ${boardTitle}`);
        },
        [],
    );

    /**
     * Handles removing items from a specific board.
     * Used by FollowSelector when a saved item is selected (to toggle off).
     *
     * @param selectedBoard - The board to remove from.
     * @param items - The item(s) to remove.
     * @param itemType - The type of items being removed.
     * @returns `true` if removal was successful.
     */
    const handleRemoveFromBoard = useCallback(
        async (
            selectedBoard: FollowSelectorItem,
            items: ItemToFollow | ItemToFollow[],
            itemType: PinItemType,
        ): Promise<boolean> => {
            const itemsToRemove = (
                Array.isArray(items) ? items : [items]
            ).filter((item) => item.id !== 0);

            if (!itemsToRemove.length) {
                return false;
            }

            let previousSavedPinKeys = new Set(savedPinKeys);
            let previousMembershipCache =
                cloneMembershipCache(pinMembershipCache);

            try {
                // Photo pins are removed by LibraryImage ID; everything else
                // by the library item (page) ID.
                const isPhotoPin = itemType === 'PhotoPin';
                const removalIds = itemsToRemove.map(({ id, libraryItemId }) =>
                    String(resolveTrackedPinId(id, libraryItemId, itemType)),
                );
                const pinKeys = itemsToRemove.map(({ id, libraryItemId }) =>
                    makePinKey(
                        resolveTrackedPinId(id, libraryItemId, itemType),
                        itemType,
                    ),
                );
                previousSavedPinKeys = new Set(savedPinKeys);
                previousMembershipCache =
                    cloneMembershipCache(pinMembershipCache);

                const optimisticResult = removePinMembership(
                    pinMembershipCache,
                    pinKeys,
                    selectedBoard.id,
                );
                setPinMembershipCache(optimisticResult.cache);
                setSavedPinKeys((prev: Set<string>) =>
                    removeSavedPinKeys(
                        prev,
                        optimisticResult.keysRemovedFromAllBoards,
                    ),
                );

                const formData = new FormData();
                formData.append('boardId', String(selectedBoard.id));
                const removalField = isPhotoPin ? 'imageIds[]' : 'pageIds[]';
                removalIds.forEach((removalId) => {
                    formData.append(removalField, removalId);
                });

                const response = await fetch(
                    REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE,
                    {
                        method: 'post',
                        credentials: 'same-origin',
                        body: formData,
                    },
                );

                if (!response.ok) {
                    throw new Error('Remove from mood board failed');
                }

                // Show success toast
                showRemoveSuccessToast(selectedBoard);

                return true;
            } catch (error) {
                console.error('Error removing items from board:', error);
                // Roll back optimistic updates when the request fails.
                setSavedPinKeys(previousSavedPinKeys);
                setPinMembershipCache(previousMembershipCache);
                toast.error('Failed to remove from board');
                return false;
            }
        },
        [
            savedPinKeys,
            pinMembershipCache,
            showRemoveSuccessToast,
            setSavedPinKeys,
            setPinMembershipCache,
        ],
    );

    return {
        handleSaveToBoard,
        handleRemoveFromBoard,
        isInDesignBoard,
    };
};
